import React, {
  Suspense,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState
} from "react";
import "bootstrap/dist/css/bootstrap.rtl.min.css";
import "bootstrap-icons/font/bootstrap-icons.scss";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import "./App.scss";
import { Routes, Route, useLocation, useNavigate } from "react-router-dom";
import MyProfile from "./screens/MyProfile/MyProfile";
import ByHandExercise from "./screens/ByHandExercise/ByHandExercise";
import BySummary from "./screens/BySummary/BySummary";
import Help from "./screens/Help/Help";
import EducationalVideo from "./screens/EducationalVideo/EducationalVideo";
import StudentFolder from "./screens/StudentFolder/StudentFolder";
import { useTypedDispatch, useTypedSelector } from "store";
import ByHandPlayExercise from "./screens/ByHandPlayExercise/ByHandExercise";
import "./styles/layoutStyles.scss";
import { setUser, setSimpleField, setWindowSize, updateUser } from "store/users/slice";
import { getSettings, getUserInfo, placeOrderFunc } from "store/users/thunkActions";
import ContactScreen from "screens/ContactScreen";
import Alert from "common/components/Alert";
import { localStorageKeys } from "config/localStorageKeys";
import { ProgramItems } from "types/landing";
import OrderValidationScreen from "screens/OrderValidationScreen";
import ThankYouAfterPayment from "screens/ThankYouAfterPayment";
import { useQuery } from "hooks/useQuery";
import { setGlobalGameState, setHideSolutionsetGame } from "store/game/slice";
import TagManager from "react-gtm-module";
import axios from "axios";
import "assets/common styles/globalAdaptive.scss";
import { showMobile, showMobileBidding } from "helpers/adaptation";

console.log("🚀 ~ showMobile:", showMobile);
import ModalTitle from "common/components/ModalTitle";
import { useOrientationHook } from "hooks/useOrientationHook";
import HorizontalScreenComponent from "common/components/HorizontalScreenComponent/HorizontalScreenComponent";
import { VideoScreen } from "screens/VideoScreen/VideoScreen";
import { useScreenOrientation } from "hooks/useScreenOrientation";

const tagManagerArgs = {
  gtmId: "GTM-TCJVB787"
};
TagManager.initialize(tagManagerArgs);

const LandingPage: any = React.lazy(() => import("./screens/Landing/LandingPage"));
const ForgotPasswordPage: any = React.lazy(() => import("./screens/ForgotPassword"));
const MainPage = React.lazy(() => import("./screens/MainPage/MainPage"));
const ProductPage = React.lazy(() => import("./screens/ProductPage"));
const Bidding = React.lazy(() => import("./screens/Bidding/Bidding"));
const HandPlay = React.lazy(() => import("./screens/HandPlay/HandPlay"));
const BiddingInteractive = React.lazy(
  () => import("./screens/BiddingInteractive/BiddingInteractive")
);
const TermsPrivacy = React.lazy(() => import("./screens/TermsPrivacy"));
const AccessibilityStatement = React.lazy(() => import("./screens/AccessibilityStatement"));
const DeffenceExercise = React.lazy(() => import("./screens/DeffenceExercise/DeffenceExercise"));
const DeffensePlay = React.lazy(() => import("./screens/DeffensePlay/DeffensePlay"));
const TricksPlay = React.lazy(() => import("./screens/TricksPlay/TricksPlay"));
const DefLeadPlay = React.lazy(() => import("./screens/DefLeadPlay/DefLeadPlay"));
const MiniBridge = React.lazy(() => import("./screens/MiniBridge/MiniBridge"));
const PaymentScreen = React.lazy(() => import("./screens/PaymentScreen"));

const exceptionUrlsNotScale = [
  // "/"
  // "/thank_you",
  // "/contact",
  // "/educational_video",
  // "/by_hand_exercise",
  // "/by_hand_play_exercise",
  // "/by_deffence_exercise",
  // "/payment",
  // "/terms_privacy",
  // "/accessibility_statement",
  // "/help",
  // "/student_folder",
  // "/bidding"
];

const GAME_PATHS = [
  "/bidding",
  "/bidding_interactive",
  "/hand_play",
  "/deffense_play",
  "/tricks_play",
  "/def_lead_play",
  "/mini_bridge"
];

declare global {
  interface Window {
    dataLayer: any;
  }
}

function App() {
  const location = useLocation();
  const navigate = useNavigate();
  const isUsed = useRef(false);
  const dispatch = useTypedDispatch();
  const [showModalSessionTimeout, setShowModalSessionTimeout] = useState(false);
  const isTermsPage = useMemo(() => {
    return (
      location.pathname.indexOf("terms_privacy") > -1 ||
      location.pathname.indexOf("accessibility_statement") > -1
    );
  }, [location]);
  const paramsQuery = useQuery();

  const isLandscape = useOrientationHook();

  const orientation = useScreenOrientation();

  const isLoggedIn = useTypedSelector((state) => state.user.isAuth);

  window?.dataLayer.push({
    event: "pageview"
  });

  const [screenWidth, setScreenWidth] = useState(window.screen.width);

  const BASE_WIDTH = 1920;
  const [appScale, setAppScale] = useState(1);
  const wrapperRef = useRef<HTMLDivElement>(null);

  const updateScale = () => {
    if (!wrapperRef.current) return;
    const widthRatio = wrapperRef.current.parentElement.clientWidth / BASE_WIDTH;
    setAppScale(Math.min(widthRatio, 1));
  };

  useEffect(() => {
    let resizeFrame: number | null = null;
    const handleResize = () => {
      if (resizeFrame !== null) window.cancelAnimationFrame(resizeFrame);
      resizeFrame = window.requestAnimationFrame(() => {
        resizeFrame = null;
        updateScale();
      });
    };

    updateScale();

    const observer =
      typeof ResizeObserver !== "undefined" ? new ResizeObserver(handleResize) : undefined;
    observer?.observe(wrapperRef.current.parentElement);
    const viewport = window.visualViewport;
    window.addEventListener("resize", handleResize);
    window.addEventListener("orientationchange", handleResize);
    viewport?.addEventListener("resize", handleResize);

    return () => {
      observer?.disconnect();
      if (resizeFrame !== null) window.cancelAnimationFrame(resizeFrame);
      window.removeEventListener("resize", handleResize);
      window.removeEventListener("orientationchange", handleResize);
      viewport?.removeEventListener("resize", handleResize);
    };
  }, []);

  useLayoutEffect(() => {
    setScreenWidth(window.screen.width);
  }, [orientation]);

  useEffect(() => {
    if (paramsQuery.get("mode") === "hide-solution") {
      dispatch(setHideSolutionsetGame(true));
    } else {
      dispatch(setHideSolutionsetGame(false));
    }

    const fetchDomain = async () => {
      const isprod = true;
      if (isprod) {
        const getLocalDomain = localStorage.getItem(localStorageKeys.domainUrl);
        try {
          const res = await axios(window.origin + "/assets/config/config.json", {
            method: "get"
          });

          if (res.data?.apiUrl) {
            localStorage.setItem(localStorageKeys.domainUrl, res.data?.apiUrl);
          } else {
            if (!getLocalDomain) {
              if (
                window.location.hostname === "test.gobeb.co.il" ||
                window.location.hostname === "localhost" ||
                window.location.hostname === "staging.d3hswvw0eucwma.amplifyapp.com"
              ) {
                localStorage.setItem(localStorageKeys.domainUrl, "https://test-api.gobeb.co.il");
              } else {
                localStorage.setItem(localStorageKeys.domainUrl, "https://api.gobeb.co.il");
              }
            }
          }
        } catch {
          if (!getLocalDomain) {
            if (
              window.location.hostname === "test.gobeb.co.il" ||
              window.location.hostname === "localhost" ||
              window.location.hostname === "staging.d3hswvw0eucwma.amplifyapp.com"
            ) {
              localStorage.setItem(localStorageKeys.domainUrl, "https://test-api.gobeb.co.il");
            } else {
              localStorage.setItem(localStorageKeys.domainUrl, "https://api.gobeb.co.il");
            }
          }
        }
      }
    };
    fetchDomain();
  }, [dispatch, paramsQuery]);
  useEffect(() => {
    const onChangeScreen = () => {
      dispatch(setWindowSize(window.innerWidth));
    };
    window.addEventListener("resize", onChangeScreen);
    return () => {
      window.removeEventListener("resize", onChangeScreen);
    };
  }, [dispatch]);

  useEffect(() => {
    if (isUsed.current) return;

    if (location.pathname === "/") {
      document.body.classList.add("body-land");
    } else {
      document.body.removeAttribute("class");
    }

    const userInfo = localStorage.getItem(localStorageKeys.userInfo);
    if (userInfo && location.pathname !== "/") {
      dispatch(setUser(JSON.parse(userInfo)));
      dispatch(setSimpleField({ type: "isAuth", value: true }));
      isUsed.current = true;
    }
  }, [dispatch, location.pathname, navigate]);

  // Listener for buy abonement
  useEffect(() => {
    const fetchData = async () => {
      try {
        // Checking if user is not in the game the we will clear all data in the game
        if (
          location.pathname !== "/hand_play" &&
          location.pathname !== "/deffense_play" &&
          location.pathname !== "/tricks_play" &&
          location.pathname !== "/mini_bridge"
        ) {
          dispatch(setGlobalGameState({}));
        }
        if (location.pathname !== "/") {
          const abonementInfoStr = localStorage.getItem(localStorageKeys.choosePrice);

          if (abonementInfoStr) {
            const abonementInfoParse: ProgramItems = JSON.parse(abonementInfoStr);

            const infoRequest: any = await dispatch(placeOrderFunc(abonementInfoParse));

            if (infoRequest?.data?.orderInfo) {
              localStorage.setItem(
                localStorageKeys.orderPlanInfo,
                JSON.stringify(infoRequest.data)
              );
            }
            if (infoRequest?.data?.paymentPage) {
              localStorage.removeItem(localStorageKeys.choosePrice);
              if (showMobile) {
                window.location.href = infoRequest.data.paymentPage;
              } else {
                window.open(infoRequest?.data?.paymentPage, "_blank");
              }
            }
          }
        }
      } catch {
        //
      }
    };
    fetchData();
  }, [dispatch, location.pathname]);

  useEffect(() => {
    const fetchFunc = async () => {
      if (isLoggedIn) {
        try {
          await dispatch(getSettings());
        } catch (err) {
          console.error("getSettings error:", err);
        }
        try {
          const res: any = await dispatch(getUserInfo());
          const user = res?.userDetails?.user;

          if (user) {
            dispatch(updateUser({ ...user, key: res?.key }));
          }

          const shouldRedirect =
            !user?.email &&
            !window.location.pathname.includes("/cp") &&
            !window.location.pathname.includes("/product_page") &&
            window.location.pathname !== "/";

          if (shouldRedirect) {
            localStorage.removeItem(localStorageKeys.userInfo);
            if (!isTermsPage) {
              navigate("/");
            }
          }
        } catch (err) {
          console.error("getUserInfo error:", err);
        }
      }
    };
    fetchFunc();
  }, [dispatch, isTermsPage, navigate, isLoggedIn]);

  const isFixSizePage = useMemo(() => {
    if (location?.pathname) {
      return !exceptionUrlsNotScale.some((val) => val === location?.pathname) && showMobile;
    } else {
      return true;
    }
  }, [location.pathname]);

  // Temporary
  const isMobileVersionWitoutGames = useMemo(() => {
    return (
      showMobile &&
      ![
        showMobileBidding ? "" : "/bidding",
        showMobileBidding ? "" : "/bidding_interactive",
        showMobileBidding ? "" : "/hand_play",
        showMobileBidding ? "" : "/tricks_play",
        showMobileBidding ? "" : "/deffense_play",
        showMobileBidding ? "" : "/def_lead_play",
        showMobileBidding ? "" : "/mini_bridge"
      ].some((val) => val === location?.pathname)
    );
  }, [location?.pathname]);

  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    const isGamePage = GAME_PATHS.includes(location.pathname);

    if (!isGamePage) {
      return;
    }

    const resetTimeout = () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }

      intervalRef.current = setInterval(() => {
        setShowModalSessionTimeout(true);
      }, 1200000);
    };

    resetTimeout();

    const handleUserActivity = () => resetTimeout();

    document.addEventListener("mousedown", handleUserActivity);
    document.addEventListener("keydown", handleUserActivity);
    document.addEventListener("mousemove", handleUserActivity);
    document.addEventListener("scroll", handleUserActivity);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
      document.removeEventListener("mousedown", handleUserActivity);
      document.removeEventListener("keydown", handleUserActivity);
      document.removeEventListener("mousemove", handleUserActivity);
      document.removeEventListener("scroll", handleUserActivity);
    };
  }, [location.pathname]);

  const scaleWidthPage = useMemo(() => {
    return isMobileVersionWitoutGames ? "auto" : screenWidth < 1900 ? 1900 : screenWidth;
  }, [isMobileVersionWitoutGames, screenWidth, orientation]);

  // show when mobile turns to horizontal orientation, except video page
  // if (
  //   showMobile &&
  //   isLandscape &&
  //   orientation === "landscape-primary" &&
  //   !location.pathname.includes("/video/") &&
  //   !location.pathname.includes("/educational_video")
  // ) {
  //   return <HorizontalScreenComponent />;
  // }

  return (
    <div
      id="scale-screen"
      style={{
        width: "100%",
        ...(!showMobile && { height: "100vh", minWidth: 0, overflowX: "clip" }),
        display: showMobile ? "block" : "flex",
        justifyContent: "center"
      }}>
      <div
        ref={wrapperRef}
        style={{
          ...(isLoggedIn && !showMobile
            ? {
                transform: `scale(${appScale})`,
                transformOrigin: "top",
                width: "1920px"
              }
            : {
                width: "100%"
              }),
          height: "100%"
        }}>
        <Alert />
        <ModalTitle
          title="אופס... ראינו שלא היית זמן רב באתר, יש ללחוץ כאן כדי לחזור לדף הראשי"
          show={showModalSessionTimeout}
          onOk={() => {
            navigate("/main_page");
            setShowModalSessionTimeout(false);
          }}
          onClose={() => {
            navigate("/main_page");
            setShowModalSessionTimeout(false);
          }}
        />
        <div
          style={{
            ...(isFixSizePage
              ? {
                  height: screenWidth <= 1100 ? "auto" : window.screen.availHeight,
                  ...(isTermsPage ? { overflowX: "hidden" } : { overflow: "hidden" })
                }
              : {}),
            ...(isLoggedIn && !showMobile
              ? {
                  width: "1920px"
                }
              : {})
          }}>
          <Suspense fallback={<>...</>}>
            <Routes>
              <Route
                path="/"
                element={
                  <React.Suspense fallback={<>...</>}>
                    <LandingPage {...{ isFixSizePage, scaleWidthPage }} />
                  </React.Suspense>
                }
              />
              <Route
                path="/cp"
                element={
                  <React.Suspense fallback={<>...</>}>
                    <ForgotPasswordPage />
                  </React.Suspense>
                }
              />
              <Route
                path="/main_page"
                element={
                  <React.Suspense fallback={<>...</>}>
                    <MainPage />
                  </React.Suspense>
                }
              />
              <Route
                path="/product_page"
                element={
                  <React.Suspense fallback={<>...</>}>
                    <ProductPage />
                  </React.Suspense>
                }
              />
              <Route
                path="/bidding"
                element={
                  <React.Suspense fallback={<>...</>}>
                    <div className={`bacground-standard ${showMobile ? "background-mobile" : ""}`}>
                      <Bidding />
                    </div>
                  </React.Suspense>
                }
              />
              <Route
                path="/bidding_interactive"
                element={
                  <React.Suspense fallback={<>...</>}>
                    <div className={`bacground-standard ${showMobile ? "background-mobile" : ""}`}>
                      <BiddingInteractive />
                    </div>
                  </React.Suspense>
                }
              />
              <Route
                path="/hand_play"
                element={
                  <React.Suspense fallback={<>...</>}>
                    <div className={`bacground-standard ${showMobile ? "background-mobile" : ""}`}>
                      <HandPlay />
                    </div>
                  </React.Suspense>
                }
              />
              <Route
                path="/my_profile"
                element={
                  <React.Suspense fallback={<>...</>}>
                    <div className={`bacground-standard ${showMobile ? "background-mobile" : ""}`}>
                      <MyProfile />
                    </div>
                  </React.Suspense>
                }
              />
              <Route
                path="/by_hand_exercise"
                element={
                  <React.Suspense fallback={<>...</>}>
                    <div className={`bacground-standard ${showMobile ? "background-mobile" : ""}`}>
                      <ByHandExercise />
                    </div>
                  </React.Suspense>
                }
              />
              <Route
                path="/by_summary"
                element={
                  <React.Suspense fallback={<>...</>}>
                    <div className={`bacground-standard ${showMobile ? "background-mobile" : ""}`}>
                      <BySummary />
                    </div>
                  </React.Suspense>
                }
              />
              <Route
                path="/by_hand_play_exercise"
                element={
                  <React.Suspense fallback={<>...</>}>
                    <div className={`bacground-standard ${showMobile ? "background-mobile" : ""}`}>
                      <ByHandPlayExercise />
                    </div>
                  </React.Suspense>
                }
              />
              <Route
                path="/help"
                element={
                  <React.Suspense fallback={<>...</>}>
                    <div>
                      <Help />
                    </div>
                  </React.Suspense>
                }
              />
              <Route
                path="/educational_video"
                element={
                  <React.Suspense fallback={<>...</>}>
                    <div className={`bacground-standard ${showMobile ? "background-mobile" : ""}`}>
                      <EducationalVideo />
                    </div>
                  </React.Suspense>
                }
              />
              <Route
                path="/student_folder"
                element={
                  <React.Suspense fallback={<>...</>}>
                    <div className={`bacground-standard ${showMobile ? "background-mobile" : ""}`}>
                      <StudentFolder />
                    </div>
                  </React.Suspense>
                }
              />
              <Route
                path="/terms_privacy"
                element={
                  <React.Suspense fallback={<>...</>}>
                    <div>
                      <TermsPrivacy />
                    </div>
                  </React.Suspense>
                }
              />
              <Route
                path="/accessibility_statement"
                element={
                  <React.Suspense fallback={<>...</>}>
                    <div>
                      <AccessibilityStatement />
                    </div>
                  </React.Suspense>
                }
              />
              <Route
                path="/terms_privacy_auth"
                element={
                  <React.Suspense fallback={<>...</>}>
                    <div>
                      <TermsPrivacy type="not_auth" />
                    </div>
                  </React.Suspense>
                }
              />
              <Route
                path="/by_deffence_exercise"
                element={
                  <React.Suspense fallback={<>...</>}>
                    <div className={`bacground-standard ${showMobile ? "background-mobile" : ""}`}>
                      <DeffenceExercise />
                    </div>
                  </React.Suspense>
                }
              />
              <Route
                path="/deffense_play"
                element={
                  <React.Suspense fallback={<>...</>}>
                    <div className={`bacground-standard ${showMobile ? "background-mobile" : ""}`}>
                      <DeffensePlay />
                    </div>
                  </React.Suspense>
                }
              />
              <Route
                path="/tricks_play"
                element={
                  <React.Suspense fallback={<>...</>}>
                    <div className={`bacground-standard ${showMobile ? "background-mobile" : ""}`}>
                      <TricksPlay />
                    </div>
                  </React.Suspense>
                }
              />
              <Route
                path="/def_lead_play"
                element={
                  <React.Suspense fallback={<>...</>}>
                    <div className={`bacground-standard ${showMobile ? "background-mobile" : ""}`}>
                      <DefLeadPlay />
                    </div>
                  </React.Suspense>
                }
              />
              <Route
                path="/mini_bridge"
                element={
                  <React.Suspense fallback={<>...</>}>
                    <div className={`bacground-standard ${showMobile ? "background-mobile" : ""}`}>
                      <MiniBridge />
                    </div>
                  </React.Suspense>
                }
              />
              <Route
                path="/payment"
                element={
                  <React.Suspense fallback={<>...</>}>
                    <div className={`bacground-standard ${showMobile ? "background-mobile" : ""}`}>
                      <PaymentScreen />
                    </div>
                  </React.Suspense>
                }
              />
              <Route
                path="/contact"
                element={
                  <React.Suspense fallback={<>...</>}>
                    <div>
                      <ContactScreen />
                    </div>
                  </React.Suspense>
                }
              />
              <Route
                path="/orders"
                element={
                  <React.Suspense fallback={<>...</>}>
                    <div>
                      <OrderValidationScreen />
                    </div>
                  </React.Suspense>
                }
              />
              <Route
                path="/thank_you"
                element={
                  <React.Suspense fallback={<>...</>}>
                    <div>
                      <ThankYouAfterPayment />
                    </div>
                  </React.Suspense>
                }
              />
              <Route
                path="/video/:url"
                element={
                  <React.Suspense fallback={<>...</>}>
                    <VideoScreen />
                  </React.Suspense>
                }
              />
            </Routes>
          </Suspense>
        </div>
      </div>
    </div>
  );
}

export default App;
